import { ToQuerySimple } from '@sprinx/query-builder';
import React from 'react';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import { ProductParameterGroupTypes } from '../../@sprinx/knihovka-types';
import {
  AfterPageProps,
  createApiClient,
  createInitialPropsRedirectToLogin,
  makeAfterComponent,
  wrapLayout,
} from '../../@sprinx/react-after-razzle';
import { createListInitialSettings } from '../../@sprinx/react-after-razzle/filteredLists';
import commonThingsToLoad from '../../api/commonThingsToLoad';
import {
  catalogueCallParamsState,
  catalogueInitialState,
  convertUrlToFilterField,
  getProductCatalogue,
} from '../../api/products/catalogue';
import {
  getProductParameterGroupTypes,
  productParameterGroupTypesInitialState,
} from '../../api/products/productParameterGroupTypes';
import ErrorInternal from '../../components/ErrorInternal';
import CartAsideLayout from '../../layouts/CartAsideLayout';
import { getShippings, shippingsState } from '../../api/shipping/shipping';
import { shoppingCustomerSelector } from '../../api/shopping';
import { useApiClient } from '../../api/appState';
import { shoppingCartState } from '../../api/shoppingCart';
import CataloguePageLayout from './CataloguePageLayout';

type CataloguePageProps = AfterPageProps;

const CataloguePage = React.forwardRef<any, CataloguePageProps>(({ error, match }, ref) => {
  const setCallParams = useSetRecoilState(catalogueCallParamsState);
  const customer = useRecoilValue(shoppingCustomerSelector);
  const apiClient = useApiClient();
  const setShippings = useSetRecoilState(shippingsState);
  const cart = useRecoilValue(shoppingCartState);

  React.useEffect(() => {
    if (customer && cart)
      getShippings(apiClient, customer, cart.id).then((response) => {
        if (response?.rows.length) setShippings(response.rows);
      });
  }, [cart?.id]);

  // this useEffect causes double call of getProductCatalogue ...
  React.useEffect(() => {
    setCallParams((prev) => {
      const txff = convertUrlToFilterField(match.url);
      const taxonomies = (typeof txff === 'function' && txff()?.taxonomies) || undefined;
      const prevTaxonomiesFn = prev.filter.find((ff) => typeof ff === 'function') as ToQuerySimple;
      const prevTaxonomies = (prevTaxonomiesFn && prevTaxonomiesFn()?.taxonomies) || undefined;
      const taxonomyIsSame = taxonomies && prevTaxonomies && taxonomies === prevTaxonomies;
      const originFunction = prev.filter.filter((ff) => typeof ff !== 'function');
      return taxonomyIsSame
        ? {
            ...prev,
            filter: [...originFunction, txff],
          }
        : {
            ...prev,
            filter: [...prev.filter.filter((ff: any) => ff.field === 'extraTaxonomies'), ...originFunction, txff],
            pagination: {
              ...(prev.pagination || {}),
              page: 1,
            },
          };
    });
  }, [match.url, setCallParams]);

  if (error) {
    return <ErrorInternal error={error} />;
  }

  return <CataloguePageLayout ref={ref} />;
});

CataloguePage.displayName = 'CataloguePage';

export default makeAfterComponent(wrapLayout(CataloguePage, CartAsideLayout), async ({ match, initialAppProps }) => {
  try {
    if (initialAppProps) {
      const appProps = initialAppProps;

      const apiClient = createApiClient({ accessToken: appProps.auth?.accessToken });

      const [parameterGroupTypes, ...commonThings] = (await Promise.all([
        getProductParameterGroupTypes(apiClient),
        ...commonThingsToLoad.promises({ apiClient }),
      ])) as [ProductParameterGroupTypes];

      const params = listInitialSettings(appProps.locale, match.url);

      const initialCatalogueState = await getProductCatalogue(
        apiClient,
        appProps.locale,
        appProps.currency,
        undefined, // decimalPrecision,
        undefined, // enabledDynamicRules,
        appProps.pricesType,
        appProps.region,
        params,
      );

      appProps.propagateToState({
        ...commonThingsToLoad.toRegister(commonThings),
        [catalogueInitialState.key]: initialCatalogueState,
        [productParameterGroupTypesInitialState.key]: parameterGroupTypes,
        [catalogueCallParamsState.key]: params,
      });

      // appProps.presaveState([catalogueCallParamsState.key]);
    }
    return {};
  } catch (error) {
    if (error.response?.status === 401) {
      return createInitialPropsRedirectToLogin({ match });
    }
    return { error };
  }
});

const listInitialSettings = (locale: string, path: string) => {
  const txFilterField = convertUrlToFilterField(path);

  return createListInitialSettings({
    orderBy: '_ordering._default', // ['displayName', locale].join('.'),
    filter: [txFilterField],
  });
};
